import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	getAuth,
	onAuthStateChanged,
	signOut,
	signInAnonymously,
	EmailAuthProvider,
	linkWithCredential,
	sendPasswordResetEmail,
} from "firebase/auth";
import { app } from "./app";
import { TUser, Token } from "src/types";
import { handleError } from "./lib";
import { reportError } from "src/lib/observability/reportError";

const auth = getAuth(app);

export const Auth = {
	auth: auth,
	getClaims: async () => {
		const idTokenResult = await auth.currentUser?.getIdTokenResult();
		return idTokenResult?.claims as Token;
	},
	setTenantId: (tenantId: string) => {
		auth.tenantId = tenantId;
	},
	createUser: async (email: string, password: string) => {
		try {
			if (auth.currentUser && auth.currentUser.isAnonymous) {
				const credential = EmailAuthProvider.credential(email, password);
				const response = await linkWithCredential(auth.currentUser, credential);
				return { success: true, user: response.user as TUser } as const;
			}
			const userCredential = await createUserWithEmailAndPassword(auth, email, password);

			return { success: true, user: userCredential.user as TUser } as const;
		} catch (error: any) {
			return handleError(error);
		}
	},
	login: async (email: string, password: string) => {
		try {
			const userCredential = await signInWithEmailAndPassword(auth, email, password);

			const claims = await Auth.getClaims();

			let newUser = Object.create(
				Object.getPrototypeOf(userCredential.user),
				Object.getOwnPropertyDescriptors(userCredential.user)
			);
			newUser = Object.assign(newUser, claims);

			return { success: true, user: newUser as TUser };
		} catch (error: any) {
			reportError(error, { operation: "auth.login" });
			return handleError(error);
		}
	},
	logout: async () => {
		try {
			await signOut(auth);

			return { success: true };
		} catch (error) {
			reportError(error, { operation: "auth.logout" });
			return { success: false, user: null };
		}
	},
	resetPassword: async (email: string) => {
		try {
			await sendPasswordResetEmail(auth, email);

			return { success: true } as const;
		} catch (error: any) {
			reportError(error, { operation: "auth.resetPassword" });
			return handleError(error);
		}
	},
	signInAnonymously: async () => {
		const user = await signInAnonymously(auth);
		return user;
	},
	onUser: (callback: (user: TUser | null) => void) => {
		const unSubscribe = onAuthStateChanged(auth, async (user) => {
			if (user && !user.isAnonymous) {
				const claims = await Auth.getClaims();

				let newUser = Object.create(
					Object.getPrototypeOf(user),
					Object.getOwnPropertyDescriptors(user)
				);
				newUser = Object.assign(newUser, claims);

				return callback?.(newUser);
			}
			callback?.(user as TUser);
		});
		return unSubscribe;
	},
};
